/* eslint-disable no-param-reassign */
import { Button, IllustratedMessage, Pagination, Progress, Stack } from '@eleveo/components-react';
import { FilterIcon, SearchNoneIcon } from '@eleveo/icons-react';
import { FormApi } from 'final-form';
import { difference, equals, intersection } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentSidebar } from './AgentSidebar';
import { AgentsFilterSidebar, AgentsSidebarFilterState } from './AgentsFilterSidebar';
import { AgentsHeaderFilterType, AgentsHeaderSearchForm } from './AgentsHeaderSearchForm';
import { AgentsTable } from './AgentsTable';
import { CONTAINER_TAG, TITLE_TAG, TOGGLE_FILTER_BUTTON_TAG } from './constants';
import { useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { ActiveFilters as ActiveFiltersNew } from '../../components/LayoutNext/components/ActiveFilters';
import { Center } from '../../components/LayoutNext/components/Center';
import { getActiveFilterValue } from '../../components/LayoutNext/components/CheckboxFilterTile';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  GetAgentsProductionSchedulesDocument,
  GetAgentsProductionSchedulesQuery,
  GetQueuesDocument,
  GetShiftTemplatesDocument,
  GetSkillsAllDocument,
  GetSupervisedAgentsDocument,
  GetSupervisedAgentsQueryVariables,
  Queue,
  ShiftTemplate,
  Skill,
  SupervisedAgentsFilter,
  Team,
  UpdateSupervisedAgentMutationVariables,
  useGetQueuesQuery,
  useGetShiftTemplatesQuery,
  useGetSkillsAllQuery,
  useGetSupervisedAgentsQuery,
  useGetSupervisedTeamsQuery,
  useGetTeamsAllQuery,
  useUpdateSupervisedAgentMutation,
} from '../../graphql/components';
import { QueuesResult, ShiftTemplatesResult, SkillsAllResult, SupervisedTeamsResult } from '../../graphql/types';
import { recordBy } from '../../utils/array';
import { useFilter } from '../../utils/filter';

import type { Agent, AgentValues } from './types';

export type UsedAgentModal =
  | { state: 'ready' }
  | {
      state: 'open';
      form: FormApi<AgentValues>;
      values: AgentValues;
    }
  | {
      state: 'proceed';
      form: FormApi<AgentValues>;
      values: AgentValues;
    };

export const defaultAgentsSidebarFilter: AgentsSidebarFilterState = {
  queues: [],
  skills: [],
  shiftTemplates: [],
  teams: [],
};

export const getAgentsWithQueues = (
  agentsData?: Agent[],
  queuesData?: Queue[],
  skillsData?: Skill[],
  shiftTemplatesData?: ShiftTemplate[],
  teamsData?: Team[]
) => {
  const agents: Agent[] = structuredClone(agentsData) ?? [];
  const queues = recordBy(queuesData ?? [], 'id');
  const skills = recordBy(skillsData ?? [], 'id');
  const shiftTemplates = recordBy(shiftTemplatesData ?? [], 'id');
  const teams = recordBy(teamsData ?? [], 'id');

  return agents.map((agent) => {
    if (agent.queueIds?.length) {
      agent.queues = agent.queueIds.map((queueId) => queues[queueId]).filter(Boolean);
    }

    if (agent.skillIds?.length) {
      agent.skills = agent.skillIds.map((skillId) => skills[skillId]).filter(Boolean);
    }

    if (agent.shiftTemplateIds) {
      agent.shiftTemplates = agent.shiftTemplateIds
        .map((shiftTemplateId) => shiftTemplates[shiftTemplateId])
        .filter(Boolean);
    }

    if (agent.teamIds?.length) {
      agent.teams = agent.teamIds.map((teamId) => teams[teamId]).filter(Boolean);
    }

    return agent;
  });
};

export const AgentsScene: React.FC = () => {
  const { t } = useTranslation();
  const { openSidebar, closeSidebar, isSidebarOpen } = useSidebar();
  const { userId } = useAuth();

  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState<Agent | null>(null);
  const [mutationInProgress, setMutationInProgress] = useState(false);
  const [usedAgentModal, setUsedAgentModal] = useState<UsedAgentModal>({ state: 'ready' });
  // Sidebar Filter Accordion open tiles state
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>([]);

  useEffect(() => {
    if (usedAgentModal.state === 'proceed') {
      const { values, form } = usedAgentModal;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleSubmitForm(values, form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedAgentModal.state]);

  const { filter: searchFilter, setFilter: setSearchFilter, isFilterActive } = useFilter<AgentsHeaderFilterType>();
  const { filter: sidebarFilter, setFilter: setSidebarFilter } =
    useFilter<AgentsSidebarFilterState>(defaultAgentsSidebarFilter);

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 25,
  });

  const supervisedAgentsFilters: SupervisedAgentsFilter = {
    agentName: searchFilter.name,
    agentSurname: searchFilter.surname,
    first: pagination.pageSize * (pagination.page - 1),
    max: pagination.pageSize,
    queueIds: sidebarFilter.queues.toString() || undefined,
    skillIds: sidebarFilter.skills.toString() || undefined,
    shiftTemplateIds: sidebarFilter.shiftTemplates.toString() || undefined,
    teamIds: sidebarFilter.teams.toString() || undefined,
  };

  const [updateAgent] = useUpdateSupervisedAgentMutation();
  const {
    client,
    data: agentsData,
    error: agentsQueryError,
    loading: isAgentsQueryLoading,
    refetch: refetchGetAgentsQuery,
  } = useGetSupervisedAgentsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    variables: {
      supervisorId: userId,
      filters: supervisedAgentsFilters,
    },
  });
  const {
    data: shiftTemplatesData,
    error: shiftTemplatesQueryError,
    loading: isShiftTemplatesQueryLoading,
  } = useGetShiftTemplatesQuery({ fetchPolicy: 'network-only' });
  const {
    data: queuesData,
    error: queuesQueryError,
    loading: isQueuesQueryLoading,
  } = useGetQueuesQuery({
    fetchPolicy: 'network-only',
  });
  const {
    data: skillsData,
    error: skillsQueryError,
    loading: isSkillsQueryLoading,
  } = useGetSkillsAllQuery({
    fetchPolicy: 'network-only',
  });
  const {
    data: teamsAllData,
    error: teamsAllQueryError,
    loading: isTeamsAllQueryLoading,
  } = useGetTeamsAllQuery({
    fetchPolicy: 'network-only',
  });
  const {
    data: teamsData,
    error: teamsQueryError,
    loading: isTeamsQueryLoading,
  } = useGetSupervisedTeamsQuery({
    fetchPolicy: 'network-only',
    variables: {
      supervisorId: userId,
    },
  });

  const loading =
    isAgentsQueryLoading ||
    isQueuesQueryLoading ||
    isShiftTemplatesQueryLoading ||
    isSkillsQueryLoading ||
    isTeamsQueryLoading ||
    isTeamsAllQueryLoading;

  const error =
    agentsQueryError ||
    queuesQueryError ||
    shiftTemplatesQueryError ||
    skillsQueryError ||
    teamsQueryError ||
    teamsAllQueryError;

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((state) => ({
      ...state,
      page,
      pageSize: pageSize ?? state.pageSize,
    }));
  };

  const resetPagination = () => {
    handlePageChange(1);
  };

  const handleSearch = ({ name, surname }: AgentsHeaderFilterType) => {
    const values = {
      name: name?.trim(),
      surname: surname?.trim(),
    };

    // Will call API twice, because it is not batched, React v18 should change that
    resetPagination();
    setSearchFilter(values);
  };

  const handleFilter = (filter: AgentsSidebarFilterState) => {
    // Will call API twice, because it is not batched, React v18 should change that
    resetPagination();
    setSidebarFilter(filter);
  };

  const handleEditAgent = (agent: Agent) => {
    setIsFilterSidebarOpen(false);

    setAgentToEdit(agent);
    openSidebar();
  };

  const handleSubmitForm = async (state: AgentValues, form: FormApi<AgentValues>) => {
    const { id } = state;
    const queueIds = state?.queueIds || [];
    const skillIds = state?.skillIds || [];
    const shiftTemplateIds = state?.shiftTemplateIds || [];
    const variables: UpdateSupervisedAgentMutationVariables = {
      id,
      supervisorId: userId,
      agent: {
        queueIds,
        shiftTemplateIds,
        skillIds,
      },
    };

    const initialQueues = form.getState().initialValues.queueIds ?? [];
    const selectedQueues = form.getState().values.queueIds ?? [];

    const removedQueues = difference(initialQueues, selectedQueues);
    const containRemovedQueues = removedQueues.length > 0;

    setMutationInProgress(true);

    try {
      if (containRemovedQueues && usedAgentModal.state === 'ready') {
        const checkFutureProductionSchedulesResult = await client.query<GetAgentsProductionSchedulesQuery>({
          query: GetAgentsProductionSchedulesDocument,
          variables: {
            parameters: { agentIds: [id], queueIds: removedQueues, startDateTime: new Date().toISOString() },
          },
          fetchPolicy: 'no-cache',
        });

        const isAgentUsedInFutureProductionSchedules =
          (checkFutureProductionSchedulesResult.data.agentsProductionSchedules?.[0]?.productionSchedules.length ?? 0) >
          0;

        if (isAgentUsedInFutureProductionSchedules) {
          setUsedAgentModal({ form, state: 'open', values: state });
          return;
        }
      }

      await updateAgent({
        variables,
        update: (cache: any) => {
          const queuesInFilter = Object.keys(sidebarFilter.queues);
          const shiftTemplatesInFilter = Object.keys(sidebarFilter.shiftTemplates);
          const skillsInFilter = Object.keys(sidebarFilter.skills);

          const isNotMatchingQueuesFilterCriteria =
            queuesInFilter.length && !intersection(queuesInFilter, queueIds).length;
          const isNotMatchingShiftTemplateFilterCriteria =
            shiftTemplatesInFilter.length && !intersection(shiftTemplatesInFilter, shiftTemplateIds).length;
          const isNotMatchingSkillsFilterCriteria =
            skillsInFilter.length && !intersection(skillsInFilter, skillIds).length;

          const shouldRefetch =
            isNotMatchingQueuesFilterCriteria ||
            isNotMatchingShiftTemplateFilterCriteria ||
            isNotMatchingSkillsFilterCriteria;

          if (shouldRefetch) {
            // refetch data to filter out agent as he doesn't meet filter criteria anymore
            refetchGetAgentsQuery({ supervisorId: userId, filters: supervisedAgentsFilters });

            return;
          }

          // agent still matches (at least partially) the filter criteria, he/she should be updated in cache
          const { queues } = cache.readQuery({
            query: GetQueuesDocument,
          });

          const { skills } = cache.readQuery({
            query: GetSkillsAllDocument,
          });

          const {
            shiftTemplates: { shiftTemplates },
          } = cache.readQuery({
            query: GetShiftTemplatesDocument,
          });

          const {
            supervisedAgents: { agents, totalCount },
          } = cache.readQuery({
            query: GetSupervisedAgentsDocument,
            variables: {
              supervisorId: userId,
              filters: supervisedAgentsFilters,
            },
          });

          const newQueues = queues.filter((queue: Queue) => queueIds.includes(queue.id));
          const newSkills = skills.filter((skill: Skill) => skillIds.includes(skill.id));
          const newShiftTemplates = shiftTemplates.filter((shiftTemplate: ShiftTemplate) =>
            shiftTemplateIds.includes(shiftTemplate.id)
          );

          const updatedAgents = agents.map((agent: Agent) => {
            if (agent.id === id) {
              return {
                ...agent,
                queueIds,
                skillIds,
                shiftTemplateIds,
                queues: newQueues,
                skills: newSkills,
                shiftTemplates: newShiftTemplates,
              };
            }

            return agent;
          });

          cache.writeQuery({
            variables: {
              supervisorId: userId,
              filters: supervisedAgentsFilters,
            } as GetSupervisedAgentsQueryVariables,
            query: GetSupervisedAgentsDocument,
            data: { supervisedAgents: { totalCount, __typename: 'Agents', agents: updatedAgents } },
          });
        },
      });

      closeSidebar();
      setAgentToEdit(null);
      setUsedAgentModal({ state: 'ready' });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setMutationInProgress(false);
    }
  };

  const handleCancelEditAgent = () => {
    closeSidebar();
    setAgentToEdit(null);
  };

  const handleUsedAgentModalSubmit = () => {
    if (usedAgentModal.state === 'open') {
      setUsedAgentModal((previousState) => {
        // Condition above is not enough for TypeScript to infer correct type here 😥
        if (previousState.state === 'open') {
          return {
            form: previousState.form,
            state: 'proceed',
            values: previousState.values,
          };
        }

        return previousState;
      });
    }
  };

  const handleUsedAgentModalClose = () => {
    if (usedAgentModal.state === 'open') {
      const { form } = usedAgentModal;
      const initialQueues = form.getState().initialValues.queueIds ?? [];

      form.change('queueIds', initialQueues);
      setUsedAgentModal({ state: 'ready' });
    }
  };

  const handleToggleFilterSidebar = () => {
    setPreOpenedItemIds([]);

    if (isFilterSidebarOpen) {
      closeSidebar();
      setIsFilterSidebarOpen(false);
    } else {
      setIsFilterSidebarOpen(true);
      setAgentToEdit(null);

      if (!isSidebarOpen) {
        openSidebar();
      }
    }
  };

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    setAgentToEdit(null);

    setIsFilterSidebarOpen(true);
    setPreOpenedItemIds([preOpenItemId]);
    openSidebar();
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setIsFilterSidebarOpen(false);
    setPreOpenedItemIds([]);
  };

  const ActiveFilters: React.FC = () => (
    <ActiveFiltersNew
      config={[
        {
          dataTestId: 'people-active-sidebar-filter-queues',
          defaultValue: [],
          id: '1',
          key: 'queues',
          name: t('queue.queues'),
          value: getActiveFilterValue(queuesData?.queues, sidebarFilter.queues),
        },
        {
          dataTestId: 'people-active-sidebar-filter-skills',
          defaultValue: [],
          id: '2',
          key: 'skills',
          name: t('skill.skills'),
          value: getActiveFilterValue(skillsData?.skills, sidebarFilter.skills),
        },
        {
          dataTestId: 'people-active-sidebar-filter-shifts',
          defaultValue: [],
          id: '3',
          key: 'shiftTemplates',
          name: t('shiftTemplate.shiftTemplate', { count: 2 }),
          value: getActiveFilterValue(shiftTemplatesData?.shiftTemplates?.shiftTemplates, sidebarFilter.shiftTemplates),
        },
        {
          dataTestId: 'people-active-sidebar-filter-teams',
          defaultValue: [],
          id: '4',
          key: 'teams',
          name: t('team.team', { count: 2 }),
          value: getActiveFilterValue(teamsData?.supervisedTeams, sidebarFilter.teams),
        },
      ]}
      sidebarFilter={sidebarFilter}
      setSidebarFilter={setSidebarFilter}
      openFilterSidebarItem={handleOpenFilterSidebarItem}
      cancelFilterSidebar={handleCancelFilterSidebar}
    />
  );

  const agents = useMemo(() => {
    if (isQueuesQueryLoading || isSkillsQueryLoading || isShiftTemplatesQueryLoading || isTeamsQueryLoading) {
      // do not render table with incomplete data...
      return [];
    }

    return getAgentsWithQueues(
      agentsData?.supervisedAgents?.agents,
      queuesData?.queues,
      skillsData?.skills,
      shiftTemplatesData?.shiftTemplates?.shiftTemplates,
      teamsAllData?.teams
    );
  }, [
    agentsData,
    isQueuesQueryLoading,
    isSkillsQueryLoading,
    isShiftTemplatesQueryLoading,
    queuesData,
    skillsData,
    shiftTemplatesData,
    isTeamsQueryLoading,
    teamsAllData,
  ]);
  const skipRendering = (loading && !agents.length) || error;

  return (
    <>
      <Header title={t('person.people')} testId={TITLE_TAG} />

      <Panel>
        <Stack direction="horizontal" className="w-full" spacing={15}>
          <AgentsHeaderSearchForm onSubmit={handleSearch} />

          <ActiveFilters />

          <Button
            isSelected={isFilterSidebarOpen}
            className="mis-auto"
            testId={TOGGLE_FILTER_BUTTON_TAG}
            onClick={handleToggleFilterSidebar}
          >
            <FilterIcon />
            {t('filter.filters')}
          </Button>
        </Stack>
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {(loading || mutationInProgress) && <Progress withOverlay />}

        {!skipRendering && agents.length > 0 && (
          <>
            <AgentsTable
              activeId={agentToEdit ? agentToEdit.id : undefined}
              agents={agents}
              onEditAgent={handleEditAgent}
              pagination={
                <Pagination
                  onPageChange={handlePageChange}
                  selectedPage={pagination.page}
                  selectedPageSize={pagination.pageSize}
                  totalItems={agentsData?.supervisedAgents?.totalCount ?? 0}
                />
              }
            />

            <ModalConfirmation
              acceptCaption={t('layout.action.proceed')}
              cancelCaption={t('layout.action.cancel')}
              isOpen={usedAgentModal.state === 'open'}
              onAccept={handleUsedAgentModalSubmit}
              onCancel={handleUsedAgentModalClose}
              title={t('layout.warning')}
            >
              {t('person.modalBodyUsedAgent')}
            </ModalConfirmation>
          </>
        )}

        {!skipRendering && agents.length === 0 && (
          <Center>
            <IllustratedMessage
              heading={
                isFilterActive || !equals(defaultAgentsSidebarFilter, sidebarFilter)
                  ? t('filter.noResultsMatch')
                  : t('person.noEntries')
              }
              icon={<SearchNoneIcon />}
            />
          </Center>
        )}
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && isFilterSidebarOpen && (
          <AgentsFilterSidebar
            handleCancel={handleCancelFilterSidebar}
            preOpenedItemIds={preOpenedItemIds}
            queuesResult={{ data: queuesData } as QueuesResult}
            setPreOpenedItemIds={setPreOpenedItemIds}
            setSidebarFilter={handleFilter}
            shiftTemplatesResult={{ data: shiftTemplatesData } as ShiftTemplatesResult}
            sidebarFilter={sidebarFilter}
            skillsResult={{ data: skillsData } as SkillsAllResult}
            teamsResult={{ data: teamsData } as SupervisedTeamsResult}
          />
        )}

        {isSidebarOpen && !isFilterSidebarOpen && agentToEdit && (
          <AgentSidebar
            agent={agentToEdit}
            handleSubmit={handleSubmitForm}
            queues={queuesData?.queues ?? []}
            skills={skillsData?.skills ?? []}
            shiftTemplates={shiftTemplatesData?.shiftTemplates?.shiftTemplates ?? []}
            handleCancel={handleCancelEditAgent}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
